import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NeuralNexusBackground from '../components/shared/NeuralNexusBackground';
import ArchitecturalHud from '../components/shared/ArchitecturalHud';
import './LandingPage.css';

// ─── Blue-tinted matrix rain background ──────────────────────────────────────
function MatrixCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const FONT_SIZE = 15;

    const CHARS = (
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz' +
      '0123456789><=>!=~[]{}|&^%$#@;: '
    ).split('');

    let cols = [];
    let drops = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      const count = Math.floor(canvas.width / FONT_SIZE);
      // re-init only new columns; preserve existing where possible
      if (count > drops.length) {
        for (let i = drops.length; i < count; i++) {
          drops.push(Math.random() * -60);
        }
      } else {
        drops.length = count;
      }
      cols = drops; // alias for clarity
    };

    resize();
    window.addEventListener('resize', resize);

    ctx.font = `${FONT_SIZE}px "Courier New", monospace`;

    const draw = () => {
      // Semi-transparent overlay creates the fading trail
      ctx.fillStyle = 'rgba(7, 11, 20, 0.022)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.font = `${FONT_SIZE}px "Courier New", monospace`;

      for (let i = 0; i < cols.length; i++) {
        const char = CHARS[Math.floor(Math.random() * CHARS.length)];
        const x = i * FONT_SIZE;
        const headY = cols[i] * FONT_SIZE;

        if (headY >= 0 && headY <= canvas.height + FONT_SIZE) {
          // Head char: near-white blue
          ctx.fillStyle = '#d6eeff';
          ctx.fillText(char, x, headY);
        }

        // Reset drop when it has cleared the screen
        if (headY > canvas.height && Math.random() > 0.988) {
          cols[i] = Math.random() * -30;
        }
        cols[i] += 0.10;
      }

      raf = requestAnimationFrame(draw);
    };

    if (prefersReduced) {
      // Single static render for reduced-motion users
      draw();
      cancelAnimationFrame(raf);
    } else {
      raf = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
        opacity: 0.22,
      }}
    />
  );
}

// ─── Scroll-fade section wrapper ─────────────────────────────────────────────
function FadeSection({ children, style }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.18 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px)' : 'translateY(16px)',
        transition: 'opacity 600ms ease, transform 600ms ease',
        ...style,
      }}
    >
      {children}
    </section>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const ctaRoute = '/micro-assessment';

  return (
    <div style={styles.page}>
      <NeuralNexusBackground opacity={0.9} zIndex={0} />
      <ArchitecturalHud mode="discovery" processing={false} title="Data Processing Bus" studentId={currentUser?.uid || ''} />

      {/* Ambient radial glow blobs — sit between bg and canvas */}
      <div aria-hidden="true" style={styles.glowBlob1} />
      <div aria-hidden="true" style={styles.glowBlob2} />

      {/* Matrix rain canvas */}
      <MatrixCanvas />

      {/* All page content above canvas */}
      <div style={styles.above}>
      <header style={styles.topBar}>
        <div className="om-brand" style={styles.brand}>THE OPERATOR METHOD</div>
        <div style={styles.topActions}>
          <button onClick={() => navigate('/login')} className="om-link-btn" style={styles.linkBtn}>Sign in</button>
          {currentUser && (
            <button onClick={() => navigate('/dashboard')} className="om-link-btn" style={styles.linkBtn}>Dashboard</button>
          )}
        </div>
      </header>

      <main style={styles.main}>
          <FadeSection style={styles.hero}>
            <div style={styles.heroAccentBar} />
            <h1 style={styles.heroTitle}>
              You're using AI.
              <br />
              It's just not working the way it should.
            </h1>
            <p style={styles.heroSubtext}>
              You have the tool. You're missing the operating system.
            </p>
            <p style={styles.microText}>Free assessment. About 2 minutes. No account needed.</p>
          </FadeSection>

        <FadeSection style={styles.block}>
          <h2 style={styles.sectionTitle}>You know that moment.</h2>
          <p style={styles.paragraph}>You ask AI for something. You get something unusable.</p>
          <p style={styles.paragraph}>You try again. Adjust the wording. Add more detail.</p>
          <p style={styles.paragraph}>It improves, slightly. But it's still not something you can actually use.</p>
          <p style={styles.paragraph}>So you either settle or do it yourself.</p>
          <p style={styles.paragraphEmphasis}>That's not a tool problem.</p>
          <p style={styles.replicationLine}>You've gotten good results before. You just can't explain why — and you can't reliably get them again.</p>
        </FadeSection>

        <FadeSection style={styles.reframeBlock}>
          <p style={styles.reframeLead}>It's a precision problem.</p>
          <p style={styles.paragraph}>AI doesn't reward effort.</p>
          <p style={styles.paragraph}>It rewards precision.</p>
          <p style={{ ...styles.paragraph, marginBottom: 0 }}>And precision is teachable.</p>
        </FadeSection>

        <FadeSection style={styles.block}>
          <p style={styles.diffLead}>This is not another course about AI tools.</p>
          <p style={styles.diffLead}>This is training on how to control outcomes.</p>
          <div style={styles.inlineList}>No prompt tricks. No technical complexity. No fluff.</div>
          <p style={{ ...styles.paragraph, marginTop: '16px', marginBottom: 0 }}>
            Just a clear system for getting AI to produce what you actually need.
          </p>
          <p style={{ ...styles.paragraphEmphasis, marginTop: '20px', marginBottom: 0 }}>
            No course can close your specific gap. Only something that can see it can.
          </p>
        </FadeSection>

        <FadeSection style={styles.crossBlock}>
          <p style={styles.crossText}>The gap doesn't care how long you've been working.</p>
          <p style={styles.paragraph}>Whether you're building your career or protecting it — the method is the same. The professionals who close this gap earliest are the ones who define what the role looks like next.</p>
        </FadeSection>

        <FadeSection style={styles.blockTight}>
          <p style={styles.curiosityText}>Before anything else — let's see exactly where your gap is.</p>
        </FadeSection>

        <FadeSection style={styles.safetyGrid}>
          <div style={styles.safetyCard}>No technical background required</div>
          <div style={styles.safetyCard}>No sales pressure</div>
          <div style={styles.safetyCard}>Built for real-world use, not theory</div>
        </FadeSection>

        <FadeSection style={styles.block}>
          <p style={styles.paragraph}>
            If AI has ever felt inconsistent, frustrating, or unpredictable, this will show you why.
          </p>
        </FadeSection>

        <FadeSection style={styles.founderQuoteBlock}>
          <p style={styles.founderQuote}>"AI didn't replace jobs. It redefined the role. This is where you learn that new role."</p>
          <p style={styles.founderAttrib}>— RM, Founder</p>
          <p style={styles.founderStill}>The founder of this program is currently enrolled in the certified level. Still completing it. Because this field does not stop moving — and neither does this course.</p>
        </FadeSection>

        <FadeSection style={styles.finalCtaBlock}>
          <p style={styles.finalText}>
            This is not a static lesson or a generic prompt library.
          </p>
          <p style={{ ...styles.paragraphEmphasis, margin: '0 0 20px 0' }}>
            A live AI instructor. A short assessment. And a clear picture of exactly where your gap is — and what closes it.
          </p>
          <button onClick={() => navigate(ctaRoute)} className="om-primary-btn" style={styles.primaryBtn}>Start the assessment — it's free</button>
        </FadeSection>
      </main>

      </div>{/* /above */}
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#070b14',
    color: '#eceff4',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    scrollBehavior: 'smooth',
    position: 'relative',
  },
  // Ambient glow blobs (behind canvas, above solid bg)
  glowBlob1: {
    position: 'fixed',
    top: '-8%',
    left: '-6%',
    width: '640px',
    height: '640px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(25, 70, 160, 0.28) 0%, transparent 68%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  glowBlob2: {
    position: 'fixed',
    bottom: '-4%',
    right: '-8%',
    width: '720px',
    height: '720px',
    borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(15, 80, 180, 0.2) 0%, transparent 68%)',
    pointerEvents: 'none',
    zIndex: 0,
  },
  // Wrapper that sits above canvas
  above: {
    position: 'relative',
    zIndex: 1,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  topBar: {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    borderBottom: '1px solid rgba(90, 160, 255, 0.12)',
    backgroundColor: 'rgba(7, 11, 20, 0.88)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 22px',
  },
  brand: {
    fontSize: '12px',
    letterSpacing: '0.14em',
    color: '#82b8f0',
    fontWeight: 700,
  },
  topActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  linkBtn: {
    border: '1px solid rgba(90, 160, 255, 0.22)',
    background: 'rgba(30, 65, 140, 0.1)',
    color: '#9abcd8',
    borderRadius: '999px',
    padding: '7px 14px',
    fontSize: '13px',
    cursor: 'pointer',
  },
  main: {
    maxWidth: '860px',
    margin: '0 auto',
    padding: '34px 18px 80px',
    display: 'grid',
    gap: '20px',
  },
  // ── Hero ─────────────────────────────────────────────────────────────────
  hero: {
    textAlign: 'center',
    padding: '34px 28px 30px',
    border: '1px solid rgba(90, 163, 255, 0.22)',
    borderRadius: '18px',
    background: 'rgba(7, 11, 22, 0.82)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    boxShadow:
      '0 0 80px rgba(40, 100, 220, 0.1), 0 8px 44px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)',
  },
  heroAccentBar: {
    width: '56px',
    height: '3px',
    background: 'linear-gradient(90deg, #3b82f6, #7dd3fc)',
    borderRadius: '999px',
    margin: '0 auto 20px',
    boxShadow: '0 0 14px rgba(100, 200, 255, 0.65), 0 0 28px rgba(60, 130, 255, 0.35)',
  },
  heroTitle: {
    margin: '0 0 16px 0',
    fontSize: 'clamp(24px, 3.8vw, 42px)',
    lineHeight: 1.08,
    letterSpacing: '-0.02em',
    fontWeight: 700,
    background: 'linear-gradient(140deg, #ffffff 0%, #b8d8ff 52%, #7ac8ff 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  heroSubtext: {
    margin: '0 auto 18px',
    fontSize: '16px',
    lineHeight: 1.65,
    color: '#a8bed8',
    maxWidth: '620px',
  },
  // ── Shared card styles ────────────────────────────────────────────────────
  block: {
    border: '1px solid rgba(90, 160, 255, 0.1)',
    borderRadius: '16px',
    padding: '28px 28px',
    background: 'rgba(7, 11, 22, 0.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow: '0 2px 24px rgba(0,0,0,0.4)',
  },
  blockTight: {
    border: '1px solid rgba(90, 160, 255, 0.1)',
    borderRadius: '16px',
    padding: '28px 28px',
    background: 'rgba(7, 11, 22, 0.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    textAlign: 'center',
    boxShadow: '0 2px 24px rgba(0,0,0,0.4)',
  },
  // ── Typography ────────────────────────────────────────────────────────────
  sectionTitle: {
    margin: '0 0 14px 0',
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: 1.24,
    letterSpacing: '-0.01em',
    background: 'linear-gradient(135deg, #e8f2ff 0%, #a8c8f0 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  paragraph: {
    margin: '0 0 12px 0',
    color: '#b0bdd0',
    fontSize: '18px',
    lineHeight: 1.68,
  },
  paragraphEmphasis: {
    margin: '8px 0 0 0',
    color: '#dfeeff',
    fontSize: '18px',
    lineHeight: 1.68,
    fontWeight: 600,
  },
  reframeBlock: {
    border: '1px solid rgba(80, 178, 255, 0.3)',
    borderRadius: '16px',
    padding: '28px 28px',
    background: 'rgba(7, 16, 36, 0.84)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow:
      '0 0 44px rgba(50, 130, 255, 0.1), 0 4px 24px rgba(0,0,0,0.45), inset 0 1px 0 rgba(80,178,255,0.07)',
  },
  reframeLead: {
    margin: '0 0 14px 0',
    color: '#7dd3fc',
    fontSize: '34px',
    fontWeight: 700,
    lineHeight: 1.15,
  },
  diffLead: {
    margin: '0 0 12px',
    fontSize: '31px',
    lineHeight: 1.24,
    fontWeight: 650,
    background: 'linear-gradient(135deg, #eef4ff 0%, #a8caff 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  },
  inlineList: {
    fontSize: '17px',
    color: '#96aac0',
    lineHeight: 1.65,
  },
  curiosityText: {
    margin: '0 0 10px',
    fontSize: '23px',
    color: '#cce0f8',
    lineHeight: 1.35,
  },
  safetyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
  },
  safetyCard: {
    border: '1px solid rgba(90, 163, 255, 0.13)',
    borderRadius: '12px',
    padding: '18px 16px',
    background: 'rgba(7, 11, 22, 0.8)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    color: '#a4b8d2',
    fontSize: '15px',
    lineHeight: 1.55,
    textAlign: 'center',
    boxShadow: '0 2px 14px rgba(0,0,0,0.35)',
  },
  replicationLine: {
    margin: '20px 0 0 0',
    color: '#7dd3fc',
    fontSize: '18px',
    lineHeight: 1.68,
    fontWeight: 600,
    fontStyle: 'italic',
  },
  crossBlock: {
    border: '1px solid rgba(201, 149, 42, 0.18)',
    borderRadius: '16px',
    padding: '28px 28px',
    background: 'rgba(7, 11, 22, 0.8)',
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    boxShadow: '0 2px 24px rgba(0,0,0,0.4)',
  },
  crossText: {
    margin: '0 0 14px',
    fontSize: '28px',
    fontWeight: 650,
    lineHeight: 1.24,
    color: '#d4a037',
  },
  founderQuoteBlock: {
    border: '1px solid rgba(201, 149, 42, 0.22)',
    borderRadius: '16px',
    padding: '34px 32px',
    background: 'rgba(7, 11, 22, 0.82)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    textAlign: 'center',
    boxShadow: '0 0 40px rgba(201, 149, 42, 0.08), 0 4px 24px rgba(0,0,0,0.45)',
  },
  founderQuote: {
    margin: '0 auto 14px',
    fontSize: 'clamp(18px, 2.4vw, 26px)',
    fontStyle: 'italic',
    lineHeight: 1.4,
    color: '#f0d596',
    maxWidth: '700px',
  },
  founderAttrib: {
    margin: '0 0 20px',
    fontSize: '14px',
    color: '#9b8a6a',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  founderStill: {
    margin: '0 auto',
    fontSize: '15px',
    color: '#8aa0b8',
    lineHeight: 1.68,
    maxWidth: '580px',
  },
  finalCtaBlock: {
    border: '1px solid rgba(90, 163, 255, 0.2)',
    borderRadius: '16px',
    padding: '34px 28px',
    background: 'rgba(7, 11, 22, 0.82)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    textAlign: 'center',
    boxShadow:
      '0 0 60px rgba(40, 100, 220, 0.1), 0 4px 30px rgba(0,0,0,0.45)',
  },
  finalText: {
    margin: '0 0 20px',
    fontSize: '25px',
    color: '#d8eaff',
    lineHeight: 1.34,
  },
  // ── CTA Button ────────────────────────────────────────────────────────────
  primaryBtn: {
    border: 'none',
    background: 'linear-gradient(135deg, #d4a037 0%, #c9952a 100%)',
    color: '#fff',
    borderRadius: '10px',
    padding: '13px 28px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: 'pointer',
    letterSpacing: '0.01em',
    boxShadow: '0 0 24px rgba(201, 149, 42, 0.48), 0 4px 14px rgba(0,0,0,0.3)',
  },
  microText: {
    margin: '10px 0 0',
    fontSize: '15px',
    color: '#9bb7d4',
    lineHeight: 1.5,
  },
  // ── Footer ────────────────────────────────────────────────────────────────
  footer: {
    borderTop: '1px solid rgba(90, 160, 255, 0.08)',
    padding: '28px 16px 36px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  footerNav: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  footerLink: {
    background: 'none',
    border: 'none',
    color: '#6e849c',
    fontSize: '13px',
    cursor: 'pointer',
  },
  footerAnchor: {
    color: '#6e849c',
    fontSize: '13px',
    textDecoration: 'none',
  },
  footerDot: {
    color: '#3a4a60',
    fontSize: '12px',
    userSelect: 'none',
  },
  footerTech: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  techBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11px',
    color: '#4d6480',
    letterSpacing: '0.03em',
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '4px',
  },
  footerCopy: {
    fontSize: '12px',
    color: '#4d6480',
  },
  footerRdw: {
    fontSize: '12px',
    color: '#4d6480',
  },
};
